import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaGoogleDrive } from 'react-icons/fa';
import { ChevronLeft, ChevronRight, FileText, Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiErrorPresentation } from '../utils/apiError';

const getUploadError = (error) => {
  const payload = error.response?.data;
  const detail = payload?.detail;
  const structured = detail && typeof detail === 'object' && !Array.isArray(detail)
    ? detail
    : payload?.error;
  const message = structured?.message
    || (typeof detail === 'string' ? detail : null)
    || payload?.message
    || error.message
    || 'Gagal mengunggah dokumen.';
  const code = structured?.code || structured?.error_code;
  const stage = structured?.stage;
  const retryHint = structured?.retryable === true
    ? 'Error ini bersifat sementara; silakan coba lagi.'
    : structured?.retryable === false
      ? 'Perbaiki penyebab di atas sebelum mencoba lagi.'
      : null;

  return {
    title: code ? `Upload gagal (${code})` : 'Upload gagal',
    text: [message, stage ? `Tahap: ${stage}.` : null, retryHint].filter(Boolean).join('\n'),
  };
};

function DokumenManagePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rag');
  const [classicDocuments, setClassicDocuments] = useState([]);
  const [classicTotal, setClassicTotal] = useState(0);
  const [classicSearch, setClassicSearch] = useState('');
  const [classicPage, setClassicPage] = useState(0);
  const [classicLimit, setClassicLimit] = useState(25);

  const [classicForm, setClassicForm] = useState({
    title: '',
    is_ocr: 'TIDAK',
    files: [],
  });
  const [showClassicUploadModal, setShowClassicUploadModal] = useState(false);
  const [showGdriveModal, setShowGdriveModal] = useState(false);
  const [gdriveFiles, setGdriveFiles] = useState([]);
  const [loadingGdrive, setLoadingGdrive] = useState(false);
  const [isGdriveAuthenticated, setIsGdriveAuthenticated] = useState(false);
  const [gdriveSearch, setGdriveSearch] = useState('');
  const [documents, setDocuments] = useState([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [submittingUpload, setSubmittingUpload] = useState(false);
  const [uploadingIds, setUploadingIds] = useState([]);
  const [queuedUploads, setQueuedUploads] = useState([]);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    files: [],
    parser_type: 'local',
  });

  // New state for Advanced Editor
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [limit, setLimit] = useState(10);
  const [loadingClassic, setLoadingClassic] = useState(true);
  const [loadingDocuments, setLoadingDocuments] = useState(true);

  const checkGdriveAuthStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/gdrive/status`, {
        withCredentials: true,
      });
      setIsGdriveAuthenticated(response.data.authenticated);
    } catch (error) {
      setIsGdriveAuthenticated(false);
    }
  };

  const fetchClassicDocuments = async (overrides = {}) => {
    const activePageValue = overrides.page ?? classicPage;
    const activeSearchValue = overrides.search ?? classicSearch;

    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/datatable/documents`, {
      params: {
        start: activePageValue * classicLimit,
        length: classicLimit,
        search: activeSearchValue,
      },
      withCredentials: true,
    });

    setClassicDocuments((response.data.data || []).map((item) => ({
      ...item,
      expanded: false,
    })));
    setClassicTotal(response.data.recordsFiltered || 0);
  };

  const fetchClassicInitialData = async (overrides = {}) => {
    setLoadingClassic(true);
    try {
      await fetchClassicDocuments(overrides);
    } catch (error) {
      Swal.fire('Oops!', error.response?.data?.message || 'Gagal memuat data dokumen.', 'error');
    } finally {
      setLoadingClassic(false);
    }
  };

  const fetchDocuments = async (overrides = {}) => {
    const activePage = overrides.page ?? page;
    const activeSearch = overrides.search ?? search;

    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/admin/datatable/rag-documents`,
      {
        params: { start: activePage * limit, length: limit, search: activeSearch },
        withCredentials: true,
        signal: overrides.signal,
        timeout: 30000,
      }
    );

    setDocuments(response.data.data || []);
    setTotalDocuments(response.data.recordsFiltered || 0);
  };

  const fetchInitialData = async (overrides = {}) => {
    if (!overrides.silent) setLoadingDocuments(true);
    try {
      await Promise.all([
        fetchDocuments(overrides),
      ]);
    } catch (error) {
      if (!overrides.silent && !axios.isCancel(error)) {
        Swal.fire('Oops!', error.response?.data?.message || 'Gagal memuat data halaman.', 'error');
      }
    } finally {
      if (!overrides.silent && !overrides.signal?.aborted) setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    fetchClassicInitialData();
    checkGdriveAuthStatus();
  }, [classicPage, classicSearch, classicLimit]);

  useEffect(() => {
    if (activeTab === 'rag') {
      const controller = new AbortController();
      fetchInitialData({ signal: controller.signal });
      return () => controller.abort();
    }
  }, [activeTab, page, search, limit]);

  const shouldPoll = useMemo(() => {
    const hasTrackedUploads = uploadingIds.length > 0;
    const hasProcessingRows = documents.some((item) =>
      ['pending', 'processing'].includes((item.chunking_status || '').toLowerCase())
    );
    return activeTab === 'rag' && (hasTrackedUploads || hasProcessingRows);
  }, [activeTab, documents, uploadingIds]);

  useEffect(() => {
    if (!shouldPoll) return undefined;

    const controller = new AbortController();
    let timer;
    const refresh = async () => {
      await fetchInitialData({ silent: true, signal: controller.signal });
      if (!controller.signal.aborted) timer = setTimeout(refresh, 3000);
    };
    timer = setTimeout(refresh, 3000);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [shouldPoll, page, search, limit]);

  useEffect(() => {
    if (uploadingIds.length === 0 || documents.length === 0) return;

    const trackedRows = documents.filter((item) => uploadingIds.includes(item.id));
    if (trackedRows.length === 0) return;

    const allFinished = trackedRows.every((item) =>
      ['parsed', 'verified', 'completed', 'failed'].includes((item.chunking_status || '').toLowerCase())
    );
    if (!allFinished) return;

    // Reset tracking once finished
    setUploadingIds([]);
    setQueuedUploads([]);
  }, [documents, uploadingIds]);

  const openUploadModal = () => {
    setUploadForm({ title: '', files: [], parser_type: 'local' });
    setQueuedUploads([]);
    setShowUploadModal(true);
  };

  const openClassicUploadModal = () => {
    setClassicForm({ title: '', is_ocr: 'TIDAK', files: [] });
    setShowClassicUploadModal(true);
  };

  const handleClassicUploadChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'files') {
      setClassicForm((prev) => ({ ...prev, files: Array.from(files || []) }));
      return;
    }

    setClassicForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClassicUploadSubmit = async (event) => {
    event.preventDefault();
    if (classicForm.files.length === 0) {
      Swal.fire('Oops!', 'Pilih minimal satu file PDF.', 'warning');
      return;
    }

    Swal.fire({
      title: 'Memuat...',
      text: 'Mohon tunggu, sedang memproses data...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const payload = new FormData();
    if (classicForm.title) payload.append('title', classicForm.title);
    payload.append('is_ocr', classicForm.is_ocr);
    classicForm.files.forEach((file) => payload.append('file', file));

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/admin/documents`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setShowClassicUploadModal(false);
      await fetchClassicInitialData();

      // Optionally open editor immediately for the last uploaded file
      // For simplicity, just show success and let user click 'Edit' in list
      Swal.fire('Berhasil', 'Dokumen berhasil diupload. Silakan klik tombol Edit untuk membagi chunk.', 'success');
    } catch (error) {
      Swal.fire('Oops!', error.response?.data?.message || 'Gagal mengupload dokumen.', 'error');
    }
  };

  const deleteClassicDocument = async (id) => {
    const result = await Swal.fire({
      title: 'Yakin ingin menghapus dokumen ini?',
      text: 'Data akan dihapus secara permanen.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, hapus!',
      cancelButtonText: 'Batal',
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: 'Memuat...',
      text: 'Mohon tunggu, sedang memproses data...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/admin/documents/${id}`, {
        withCredentials: true,
      });
      await fetchClassicInitialData();
      Swal.fire('Berhasil', 'Dokumen berhasil dihapus.', 'success');
    } catch (error) {
      Swal.fire('Oops!', error.response?.data?.message || 'Gagal menghapus dokumen.', 'error');
    }
  };

  const handleGdriveUpload = async () => {
    if (isGdriveAuthenticated) {
      setShowGdriveModal(true);
      fetchGdriveFiles();
      return;
    }

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/gdrive/auth`, {
        withCredentials: true,
      });
      window.location.href = response.data.url;
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Gagal terhubung ke Google Drive', 'error');
    }
  };

  const fetchGdriveFiles = async () => {
    setLoadingGdrive(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/gdrive/files`, {
        withCredentials: true,
      });
      setGdriveFiles(response.data.files || []);
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Gagal ambil file dari Google Drive', 'error');
    } finally {
      setLoadingGdrive(false);
    }
  };

  const downloadFromGdrive = async (file) => {
    try {
      const result = await Swal.fire({
        title: 'Apakah anda yakin akan mengunduh file ini?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      });

      if (result.isDismissed) return;

      Swal.fire({
        title: 'Memuat...',
        text: 'Mohon tunggu, sedang memproses data...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/admin/gdrive/download`,
        { fileId: file.id, fileName: file.name, is_ocr: 'YA' },
        { withCredentials: true }
      );

      setShowGdriveModal(false);
      await fetchClassicInitialData();
      Swal.fire('Berhasil', `File ${file.name} berhasil diupload`, 'success');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Gagal download dari Google Drive', 'error');
    }
  };

  const handleGdriveLogout = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/gdrive/logout`, {
        withCredentials: true,
      });
      setIsGdriveAuthenticated(false);
      setGdriveFiles([]);
      Swal.fire('Logout', 'Berhasil logout dari Google Drive', 'info');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Gagal logout dari Google Drive', 'error');
    }
  };

  const handleUploadChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'files') {
      setUploadForm((prev) => ({
        ...prev,
        files: Array.from(files || []),
      }));
      return;
    }

    setUploadForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUploadSubmit = async (event) => {
    event.preventDefault();
    if (submittingUpload) return;

    if (uploadForm.files.length === 0) {
      Swal.fire('Oops!', 'Pilih minimal satu file PDF.', 'warning');
      return;
    }

    const payload = new FormData();
    payload.append('title', uploadForm.title);
    payload.append('parser_type', uploadForm.parser_type);
    uploadForm.files.forEach((file) => payload.append('files', file));

    setSubmittingUpload(true);

    Swal.fire({
      title: 'Sedang Mengunggah Dokumen...',
      text: 'Mengirim file PDF. Pemrosesan dokumen akan berjalan di latar belakang setelah upload selesai.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/upload`,
        payload,
        {
          withCredentials: true,
        }
      );

      const queuedDocs = response.data.documents || [];
      setSearch('');
      setPage(0);
      setQueuedUploads(queuedDocs);
      setUploadingIds(queuedDocs.map((item) => item.id));
      setUploadForm((prev) => ({ ...prev, files: [] }));
      setShowUploadModal(false);

      await fetchInitialData({ page: 0, search: '', silent: true });
      Swal.fire('Upload berhasil', response.data.message || 'Dokumen sedang diproses. Status akan diperbarui otomatis di tabel.', 'success');
    } catch (error) {
      const uploadError = getUploadError(error);
      Swal.fire({ ...uploadError, icon: 'error' });
      setShowUploadModal(true);
    } finally {
      setSubmittingUpload(false);
    }
  };


  const handleDeleteDocument = async (id) => {
    const result = await Swal.fire({
      title: 'Hapus dokumen ini?',
      text: 'Vector di Qdrant, chunk di MySQL, dan file PDF akan ikut dihapus.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, hapus',
      cancelButtonText: 'Batal',
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/${id}`, {
        withCredentials: true,
      });
      await fetchInitialData();
      Swal.fire('Berhasil', 'Dokumen berhasil dihapus.', 'success');
    } catch (error) {
      Swal.fire('Oops!', error.response?.data?.message || 'Gagal menghapus dokumen.', 'error');
    }
  };

  const sendDocumentToQdrant = async (documentId) => {
    const result = await Swal.fire({
      title: 'Kirim chunk ke Qdrant?',
      text: 'Pastikan struktur BAB/Pasal dan isi pasal sudah diverifikasi. Proses ini akan mengganti point Qdrant lama untuk dokumen ini.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Kirim ke Qdrant',
      cancelButtonText: 'Batal',
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/${documentId}/qdrant`,
        {},
        { withCredentials: true }
      );
      await fetchInitialData();
      Swal.fire('Berhasil', response.data.message || 'Dokumen berhasil dikirim ke Qdrant.', 'success');
    } catch (error) {
      const apiError = getApiErrorPresentation(error, 'Gagal mengirim ke Qdrant.');
      Swal.fire({ ...apiError, icon: 'error' });
    }
  };

  const getVerificationBadge = (item) => {
    const status = (item.verification_status || '').toLowerCase();
    if (status === 'synced') return <span className="badge bg-success">QDRANT SYNCED</span>;
    if (status === 'verified') return <span className="badge bg-info text-dark">VERIFIED</span>;
    if (status === 'needs_review') return <span className="badge bg-warning text-dark">NEED REVIEW</span>;
    return <span className="badge bg-secondary">DRAFT</span>;
  };

  const getStatusBadge = (status) => {
    const normalized = (status || 'pending').toLowerCase();

    if (normalized === 'completed') {
      return <span className="badge bg-success">COMPLETED</span>;
    }
    if (normalized === 'verified') {
      return <span className="badge bg-info text-dark">VERIFIED</span>;
    }
    if (normalized === 'parsed') {
      return <span className="badge bg-warning text-dark">PARSED</span>;
    }
    if (normalized === 'failed') {
      return <span className="badge bg-danger">FAILED</span>;
    }
    if (normalized === 'processing') {
      return <span className="badge bg-primary">PROCESSING</span>;
    }
    return <span className="badge bg-secondary">PENDING</span>;
  };

  const openAdvancedEditor = async (id) => {
    try {
      Swal.fire({ title: 'Memuat...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/documents/${id}`, { withCredentials: true });
      setEditingDoc(response.data);
      setEditorContent(response.data.content || '');
      setShowEditorModal(true);
      Swal.close();
    } catch (error) {
      Swal.fire('Error', 'Gagal memuat data dokumen.', 'error');
    }
  };

  const insertSeparator = () => {
    const textarea = document.getElementById('advanced-editor-textarea');
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editorContent;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const separator = `\n--- [CHUNK_SEPARATOR] BAB:  PASAL:  ---\n`;
    setEditorContent(before + separator + after);
    textarea.focus();
  };

  const handleSyncToQdrant = async () => {
    if (!editingDoc) return;

    // Parse content into chunks based on separator
    const rawChunks = editorContent.split(/--- \[CHUNK_SEPARATOR\] /);
    const processedChunks = rawChunks.map((c, idx) => {
      if (idx === 0) return { text: c.trim(), bab: '', pasal: '' };

      const match = c.match(/BAB:\s*(.*?)\s+PASAL:\s*(.*?)\s+---\n?([\s\S]*)/i);
      if (match) {
        return {
          bab: match[1].trim(),
          pasal: match[2].trim(),
          text: match[3].trim()
        };
      }
      return { text: c.trim(), bab: '', pasal: '' };
    }).filter(c => c.text);

    if (processedChunks.length === 0) {
      Swal.fire('Oops!', 'Tidak ada konten untuk disimpan.', 'warning');
      return;
    }

    setIsSyncing(true);
    Swal.fire({ title: 'Sinkronisasi...', text: 'Sedang mengirim data ke Qdrant...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/admin/documents/${editingDoc.id}/sync-qdrant`, {
        chunks: processedChunks
      }, { withCredentials: true });

      setShowEditorModal(false);
      await fetchClassicInitialData();
      Swal.fire('Berhasil', 'Data berhasil disinkronkan ke Qdrant.', 'success');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Gagal sinkronisasi ke Qdrant.', 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!editingDoc) return;

    // Parse content into chunks for DB consistency (optional but helps)
    const rawChunks = editorContent.split(/--- \[CHUNK_SEPARATOR\] /);
    const processedChunks = rawChunks.map((c, idx) => {
      if (idx === 0) return { text: c.trim() };
      const match = c.match(/BAB:\s*(.*?)\s+PASAL:\s*(.*?)\s+---\n?([\s\S]*)/i);
      if (match) return { text: match[3].trim() };
      return { text: c.trim() };
    }).filter(c => c.text);

    setIsSavingDraft(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/admin/documents/${editingDoc.id}/save-draft`, {
        content: editorContent,
        chunks: processedChunks
      }, { withCredentials: true });

      await fetchClassicInitialData();
      Swal.fire('Berhasil', 'Draf berhasil disimpan ke database.', 'success');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Gagal menyimpan draf.', 'error');
    } finally {
      setIsSavingDraft(false);
    }
  };



  const trackedRows = documents.filter((item) => uploadingIds.includes(item.id));
  const progressRows = trackedRows.length > 0 ? trackedRows : queuedUploads;

  return (
    <div className="container-fluid px-0">
      {activeTab === 'documents' && (
        <div className="alert alert-info border-0 shadow-sm mb-3">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div>
              <strong>Dokumen Umum</strong>
              <div className="small mb-0">
                Gunakan ini untuk upload dokumen biasa.
              </div>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={openClassicUploadModal}>
              Upload Dokumen Umum
            </button>
          </div>
        </div>
      )}

      {activeTab === 'documents' ? (
        <>
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-end justify-content-between gap-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Tampilkan</span>
                  <select className="form-select form-select-sm" style={{ width: 'auto' }} value={classicLimit} onChange={(e) => { setClassicLimit(Number(e.target.value)); setClassicPage(0); }}>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="text-muted small">data</span>
                </div>
                <div className="col-lg-5 p-0" style={{ minWidth: '280px', maxWidth: '360px' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Cari Judul atau File Name..."
                    value={classicSearch}
                    onChange={(event) => {
                      setClassicPage(0);
                      setClassicSearch(event.target.value);
                    }}
                  />
                </div>
                <div className="col-lg-7">
                  <div className="d-flex flex-wrap justify-content-lg-end gap-2">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover table-striped table-bordered table-sm mb-0">
                  <thead>
                    <tr>
                      <th className="bg-light text-center" width="50">No</th>
                      <th className="bg-light">Judul</th>
                      <th className="bg-light">Content</th>
                      <th className="bg-light text-center" width="10%">File</th>
                      <th className="bg-light text-center" width="10%">Chunk Count</th>
                      <th className="bg-light text-center" width="15%">Upload Date</th>
                      <th className="text-center bg-light" width="10%">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingClassic ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          Sedang meload data...
                        </td>
                      </tr>
                    ) : classicDocuments.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          Belum ada dokumen.
                        </td>
                      </tr>
                    ) : (
                      classicDocuments.map((item, index) => (
                        <tr key={item.id}>
                          <td className="text-center">{(classicPage * classicLimit) + index + 1}</td>
                          <td>{item.title}</td>
                          <td>
                            {item.content ? (
                              item.content.length > 200 ? (
                                <div>
                                  <span>
                                    {item.expanded ? item.content : `${item.content.substring(0, 200)}...`}
                                  </span>
                                </div>
                              ) : (
                                <span>{item.content}</span>
                              )
                            ) : (
                              <em>Tidak ada konten</em>
                            )}
                          </td>
                          <td>
                            {item.file_name ? (
                              <div className="text-center">
                                <a
                                  className="btn btn-sm btn-outline-dark"
                                  href={`${process.env.REACT_APP_API_BASE_URL}/admin/documents/view/${item.file_name}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="fa fa-eye"></i> Preview
                                </a>
                              </div>
                            ) : null}
                          </td>
                          <td className="text-center">
                            <b>{item.chunk_count || 0}</b>
                          </td>
                          <td className="text-center">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString('id-ID', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                title="Edit & Chunk"
                                onClick={() => openAdvancedEditor(item.id)}
                              >
                                <i className="fa fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => deleteClassicDocument(item.id)}
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer bg-white d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
              <small className="text-muted">Showing {Math.min(classicTotal, (classicPage * classicLimit) + 1)} to {Math.min(classicTotal, (classicPage + 1) * classicLimit)} of {classicTotal} entries</small>
              <div className="d-inline-flex align-items-center gap-2 rounded-pill border border-slate-200 bg-white px-2 py-1 shadow-sm">
                <button className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center" disabled={classicPage === 0} onClick={() => setClassicPage((prev) => prev - 1)} aria-label="Halaman sebelumnya"><ChevronLeft size={18} /></button>
                <span className="px-2 small text-muted">{classicPage + 1} / {Math.max(1, Math.ceil(classicTotal / classicLimit))}</span>
                <button className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center" disabled={(classicPage + 1) * classicLimit >= classicTotal} onClick={() => setClassicPage((prev) => prev + 1)} aria-label="Halaman berikutnya"><ChevronRight size={18} /></button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-slate-800">
              <FileText size={18} />
              <h4 className="mb-0 text-lg font-semibold">Daftar Dokumen</h4>
            </div>

            <div className="mb-4 d-flex flex-wrap align-items-end justify-content-between gap-3">
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-muted">Tampilkan</span>
                  <select className="form-select form-select-sm rounded-lg border-slate-300" style={{ width: '88px' }} value={limit} onChange={(e) => { setPage(0); setLimit(Number(e.target.value)); }}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="small text-muted">data</span>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-end gap-3">
                <div className="min-w-[280px] max-w-[360px] flex-1">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search size={16} /></span>
                    <input
                      type="text"
                      className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                      placeholder="Cari judul atau nama file..."
                      value={search}
                      onChange={(event) => {
                        setPage(0);
                        setSearch(event.target.value);
                      }}
                    />
                  </div>
                </div>
                <button className="btn btn-dark d-inline-flex align-items-center gap-2" onClick={openUploadModal}>
                  <Plus size={16} /> Upload Dokumen
                </button>
              </div>
            </div>

            <div className="table-responsive rounded-xl border border-slate-300 bg-white">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-center" width="50">No</th>
                    <th>Dokumen</th>
                    <th className="text-center">Chunk</th>
                    <th className="text-center">Qdrant</th>
                    <th>Tanggal</th>
                    <th className="text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingDocuments ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        Sedang meload data...
                      </td>
                    </tr>
                  ) : documents.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        Belum ada dokumen yang diproses.
                      </td>
                    </tr>
                  ) : (
                    documents.map((item, index) => {
                      return (
                        <tr key={item.id}>
                          <td className="text-center">{(page * limit) + index + 1}</td>
                          <td>
                            <div className="fw-semibold">{item.title}</div>
                            <div className="small text-muted mt-1">{item.processing_stage}</div>
                            {item.last_chunk_error ? (
                              <div className="small text-danger mt-1">{item.last_chunk_error}</div>
                            ) : null}
                          </td>
                          <td className="text-center">{item.chunk_count || 0}</td>
                          <td className="text-center">
                            <span className={`badge ${(item.qdrant_point_count || 0) > 0 ? 'bg-success' : 'bg-secondary'}`}>
                              {item.qdrant_point_count || 0} point
                            </span>
                          </td>
                          <td>
                            <div className="small">
                              {item.created_at ? new Date(item.created_at).toLocaleString('id-ID', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              }) : '-'}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {item.file_name ? (
                                <a
                                  className="btn btn-sm btn-outline-dark d-inline-flex align-items-center justify-content-center"
                                  href={`${process.env.REACT_APP_API_BASE_URL}/admin/documents/view/${item.file_name}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Lihat dokumen"
                                >
                                  <FileText size={14} />
                                </a>
                              ) : null}
                              <button
                                className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-1"
                                title="Buka review OCR dan verifikasi chunk"
                                disabled={['pending', 'processing'].includes((item.chunking_status || '').toLowerCase())}
                                onClick={() => navigate(`/dashboard/manage/document/${item.id}/chunks`)}
                              >
                                <i className="fa fa-magic"></i>
                                <span>Review</span>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-1"
                                title="Lihat vector dokumen di Qdrant"
                                onClick={() => navigate(`/dashboard/manage/document/${item.id}/qdrant`)}
                              >
                                <i className="fa fa-database"></i>
                                <span>Qdrant</span>
                              </button>
                              <button className="btn btn-sm btn-outline-danger" disabled={['pending', 'processing'].includes((item.chunking_status || '').toLowerCase())} onClick={() => handleDeleteDocument(item.id)}>
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-start align-items-center gap-3 text-sm text-slate-500">
              <small>Total: {totalDocuments}</small>
              <div className="d-inline-flex align-items-center gap-2 rounded-pill border border-slate-200 bg-white px-2 py-1 shadow-sm">
                <button className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center" disabled={page === 0} onClick={() => setPage((prev) => prev - 1)} aria-label="Halaman sebelumnya"><ChevronLeft size={18} /></button>
                <span className="px-2 small text-muted">{page + 1} / {Math.max(1, Math.ceil(totalDocuments / limit))}</span>
                <button className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center" disabled={(page + 1) * limit >= totalDocuments} onClick={() => setPage((prev) => prev + 1)} aria-label="Halaman berikutnya"><ChevronRight size={18} /></button>
              </div>
            </div>
          </div>
        </>
      )}

      {showClassicUploadModal ? (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <form onSubmit={handleClassicUploadSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Upload Dokumen</h5>
                  <button type="button" className="btn-close" onClick={() => setShowClassicUploadModal(false)}></button>
                </div>
                <div className="modal-body row g-2">
                  <div className="col-md-12">
                    <label>Judul Umum (Opsional)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={classicForm.title}
                      onChange={handleClassicUploadChange}
                      placeholder="Misal: Dokumen Hukum 2025"
                    />
                  </div>
                  <div className="col-md-12">
                    <label>Pilih File PDF (Bisa lebih dari satu)</label>
                    <input
                      type="file"
                      className="form-control"
                      name="files"
                      accept=".pdf"
                      multiple
                      onChange={handleClassicUploadChange}
                      required
                    />
                  </div>
                  <div className="col-md-12" hidden>
                    <label>Gunakan Fungsi OCR (Optical Character Recognition)</label>
                    <select className="form-control" name="is_ocr" value={classicForm.is_ocr} onChange={handleClassicUploadChange} required>
                      <option value="TIDAK">Tidak</option>
                      <option value="YA">Ya</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="submit" className="btn btn-dark">Upload</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowClassicUploadModal(false)}>
                    Tutup
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {showGdriveModal ? (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Pilih File PDF dari Google Drive</h5>
                <button type="button" className="btn-close" onClick={() => setShowGdriveModal(false)}></button>
              </div>
              <div className="modal-body">
                {!isGdriveAuthenticated ? (
                  <>
                    <div className="alert alert-info">Klik tombol di bawah untuk login ke Google Drive.</div>
                    <button className="btn btn-sm btn-dark mb-3" onClick={handleGdriveUpload}>
                      Login Ulang
                    </button>
                  </>
                ) : null}

                {isGdriveAuthenticated ? (
                  loadingGdrive ? (
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button className="btn btn-sm btn-dark mb-3" onClick={fetchGdriveFiles}>
                        Muat Ulang File PDF
                      </button>
                      <div className="mb-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Cari nama file..."
                          value={gdriveSearch}
                          onChange={(event) => setGdriveSearch(event.target.value)}
                        />
                      </div>
                      <div className="list-group" style={{ maxHeight: 400, overflowY: 'auto' }}>
                        {gdriveFiles
                          .filter((file) => file.name.toLowerCase().includes(gdriveSearch.toLowerCase()))
                          .map((file) => (
                            <div
                              key={file.id}
                              className="list-group-item d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2"
                            >
                              <label className="mb-0">
                                <i className="fa fa-file me-2"></i>
                                {file.name}
                              </label>
                              <button
                                className="btn btn-sm btn-outline-dark align-self-md-end"
                                onClick={() => downloadFromGdrive(file)}
                              >
                                <i className="fa fa-download"></i> Download to Local
                              </button>
                            </div>
                          ))}
                        {gdriveFiles.length === 0 ? (
                          <div className="list-group-item">Belum ada file PDF</div>
                        ) : null}
                      </div>
                    </>
                  )
                ) : null}
              </div>
              <div className="modal-footer">
                {isGdriveAuthenticated ? (
                  <button className="btn btn-outline-dark" onClick={handleGdriveLogout}>
                    Logout
                  </button>
                ) : null}
                <button className="btn btn-secondary" onClick={() => setShowGdriveModal(false)}>
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showUploadModal ? (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content border-0 shadow">
              <form onSubmit={handleUploadSubmit}>
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-bold text-dark">Upload Dokumen</h5>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setShowUploadModal(false)} disabled={submittingUpload}></button>
                </div>
                <div className="modal-body position-relative">
                  <div className="row g-3">
                    <div className="col-lg-6">
                      <label className="form-label">Judul Umum</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={uploadForm.title}
                        onChange={handleUploadChange}
                        placeholder="Contoh: Dokumen Peraturan 2026"
                        disabled={submittingUpload}
                      />
                    </div>
                    <div className="col-lg-6">
                      <label className="form-label">File PDF</label>
                      <input
                        type="file"
                        className="form-control"
                        name="files"
                        accept=".pdf"
                        multiple
                        onChange={handleUploadChange}
                        disabled={submittingUpload}
                        required
                      />
                      <div className="small text-muted mt-2">
                        Jika memakai `llamaparse`, pastikan minimal satu Llama Cloud API key aktif tersedia.
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <label className="form-label">Parser</label>
                      <select
                        className="form-control"
                        name="parser_type"
                        value={uploadForm.parser_type}
                        onChange={handleUploadChange}
                        disabled={submittingUpload}
                      >
                        <option value="local">Legal Parser</option>
                        <option value="llamaparse">LlamaParse</option>
                      </select>
                    </div>
                  </div>

                  {progressRows.length > 0 ? (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Progress Proses</h6>
                        <span className="small text-muted">
                          {trackedRows.length > 0 ? 'Realtime dari backend' : 'Menunggu sinkronisasi data'}
                        </span>
                      </div>
                      <div className="list-group">
                        {progressRows.map((item) => {
                          const liveItem = trackedRows.find((row) => row.id === item.id) || item;
                          const progress = Math.max(0, Math.min(100, Number(liveItem.processing_progress || 0)));

                          return (
                            <div key={item.id} className="list-group-item">
                              <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                                <div>
                                  <div className="fw-semibold">{liveItem.title || item.title}</div>
                                  <div className="small text-muted">{liveItem.file_name || item.file_name}</div>
                                </div>
                                {getStatusBadge(liveItem.chunking_status)}
                              </div>
                              <div className="progress" style={{ height: 12 }}>
                                <div
                                  className={`progress-bar ${(liveItem.chunking_status || '').toLowerCase() === 'failed' ? 'bg-danger' : 'bg-dark'}`}
                                  style={{ width: `${progress}%` }}
                                ></div>
                              </div>
                              <div className="small text-muted mt-2">
                                {liveItem.processing_stage || 'Menunggu proses'} ({progress}%)
                              </div>
                              {liveItem.last_chunk_error ? (
                                <div className="small text-danger mt-1">{liveItem.last_chunk_error}</div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <button
                    type="submit"
                    className="btn btn-dark"
                    disabled={submittingUpload}
                  >
                    {submittingUpload ? 'Mengantrekan...' : 'Upload dan Proses'}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowUploadModal(false)} disabled={submittingUpload}>
                    Tutup
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      {showEditorModal && editingDoc ? (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal d-block" tabIndex="-1" style={{ zIndex: 1060 }}>
            <div className="modal-dialog modal-xl" style={{ maxWidth: '98%', margin: '1.75rem auto' }}>
              <div className="modal-content border-0 shadow-lg" style={{ height: '90vh' }}>
                <div className="modal-header bg-dark text-bold px-4 py-3 border-0">
                  <h5 className="modal-title d-flex align-items-center">
                    <i className="fa fa-edit me-2"></i>
                    <strong>Advanced Chunk Editor: {editingDoc.title}</strong>
                  </h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger px-3 d-flex align-items-center"
                    onClick={() => setShowEditorModal(false)}
                    style={{ fontWeight: 'bold' }}
                  >
                    <i className="fa fa-times me-2"></i> Tutup
                  </button>
                </div>
                <div className="modal-body bg-light p-4" style={{ overflowY: 'auto' }}>
                  <div className="alert alert-info py-2 small mb-3 border-0 shadow-sm">
                    <i className="fa fa-info-circle me-2"></i>
                    Gunakan tombol <strong>Tambah Separator</strong> untuk membagi dokumen menjadi beberapa chunk.
                    Isi informasi <strong>BAB</strong> dan <strong>PASAL</strong> di dalam separator sebagai metadata.
                    Klik <strong>Simpan Draf</strong> untuk menyimpan progres Anda ke database.
                  </div>

                  <div className="mb-3">
                    <button className="btn btn-dark shadow-sm" onClick={insertSeparator}>
                      <i className="fa fa-plus-circle me-2"></i> Tambah Separator Chunk
                    </button>
                  </div>

                  <div className="editor-container shadow-sm bg-white rounded">
                    <textarea
                      id="advanced-editor-textarea"
                      className="form-control font-monospace border-0 p-3"
                      style={{
                        fontSize: '15px',
                        lineHeight: '1.7',
                        height: '65vh',
                        resize: 'none',
                        outline: 'none',
                        boxShadow: 'none'
                      }}
                      value={editorContent}
                      onChange={(e) => setEditorContent(e.target.value)}
                      placeholder="Paste or edit document text here..."
                    />
                  </div>
                </div>
                <div className="modal-footer bg-white border-top px-4 py-3">
                  <div className="d-flex justify-content-between w-100 align-items-center">
                    <button type="button" className="btn btn-outline-secondary px-4" onClick={() => setShowEditorModal(false)}>
                      Batal & Tutup
                    </button>
                    <div className="d-flex gap-3">
                      <button
                        type="button"
                        className="btn btn-primary px-4 shadow-sm"
                        onClick={handleSaveDraft}
                        disabled={isSavingDraft || isSyncing}
                      >
                        {isSavingDraft ? (
                          <><span className="spinner-border spinner-border-sm me-2"></span>Menyimpan...</>
                        ) : (
                          <><i className="fa fa-save me-2"></i>Simpan Draf</>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-success px-4 shadow-sm"
                        onClick={handleSyncToQdrant}
                        disabled={isSyncing || isSavingDraft}
                      >
                        {isSyncing ? (
                          <><span className="spinner-border spinner-border-sm me-2"></span>Sinkronisasi...</>
                        ) : (
                          <><i className="fa fa-cloud-upload me-2"></i>Kirim ke Qdrant</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}



    </div>
  );
}

export default DokumenManagePage;
